import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

// Preserve Shopify typo, brand and word-order matching across every search entry point.
export function displaySearchTerm(value) {
  if (value.startsWith('title:')) {
    try {
      const phrase = JSON.parse(value.slice(6));
      if (typeof phrase === 'string') return phrase;
    } catch { /* Preserve explicit advanced queries. */ }
  }
  return value;
}

export function buildSearchURL(value, { form } = {}) {
  const url = new URL(form?.action || Theme.routes.search_url, window.location.href);
  if (form) {
    for (const [key, entry] of new FormData(form)) {
      if (typeof entry === 'string') url.searchParams.append(key, entry);
    }
  }
  // Search syntax such as title:"..." disables Shopify typo tolerance.
  // Submit the shopper's words unchanged by field or phrase restrictions.
  const query = value.trim().replace(/\s+/g, ' ');
  url.searchParams.set('q', query);
  url.searchParams.set('sort_by', 'relevance');
  url.searchParams.set('options[prefix]', 'last');
  url.searchParams.delete('page');
  const current = new URL(window.location.href);
  for (const key of ['preview_theme_id', '_ab', '_fd', '_sc']) {
    if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
  }
  return url;
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (event.defaultPrevented || !(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') return;
  if (!form.matches('.search-page-input__parent, .predictive-search-form, .nsss-direct__form')) return;
  const input = form.querySelector('input[name="q"]');
  if (!(input instanceof HTMLInputElement)) return;
  const action = new URL(form.action, window.location.href);
  const search = new URL(Theme.routes.search_url, window.location.href);
  if (action.origin !== search.origin || action.pathname !== search.pathname) return;
  event.preventDefault();
  window.location.assign(buildSearchURL(input.value, { form }).toString());
});

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.searchPageInput.value = displaySearchTerm(this.refs.searchPageInput.value);
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  openSuggestions(event) {
    const input = this.refs.searchPageInput;
    const predictive = document.querySelector('#search-modal predictive-search-component');
    if (typeof predictive?.openWithQuery === 'function' && predictive.openWithQuery(input.value)) {
      event.preventDefault();
    }
  }

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}

// Preserve the shopper's query and non-facet options when removing search filters.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[data-nsss-clear-search-filters]') : null;
  if (!link) return;
  const url = new URL(window.location.href);
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
  }
  event.preventDefault();
  window.location.assign(url.toString());
});
